/**
 * InvitePage — Accept or decline a person invite link (ADR-036 Phase 2 & 3).
 *
 * Phase 2: Existing users accept/decline via Firebase JWT.
 * Phase 3: New users (no org) are shown an org creation form post-acceptance.
 *
 * Reads ?token= from the URL, fetches invite info from the public API,
 * and presents Accept / Decline actions. If the user is not signed in
 * they are redirected to /login with a returnTo parameter.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth, auth } from '../auth';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

interface InviteInfo {
  person_name: string;
  inviter_org_name: string;
  email: string;
  is_expired: boolean;
  is_claimed: boolean;
}

type PageState =
  | 'loading'
  | 'ready'
  | 'expired'
  | 'claimed'
  | 'accepted'
  | 'accepted_needs_org'
  | 'org_registered'
  | 'declined'
  | 'error';

type OrgType = 'hirer' | 'partner' | 'supplier';

interface OrgFormState {
  orgName: string;
  orgSlug: string;
  orgType: OrgType;
  consentTerms: boolean;
  consentPrivacy: boolean;
  consentMarketing: boolean;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'org';
}

export function InvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const [orgForm, setOrgForm] = useState<OrgFormState>({
    orgName: '',
    orgSlug: '',
    orgType: 'hirer',
    consentTerms: false,
    consentPrivacy: false,
    consentMarketing: false,
  });
  const [orgRegistering, setOrgRegistering] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [registeredSlug, setRegisteredSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/v1/public/people/invites/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        if (!res.ok) { setPageState('error'); return; }
        const data: InviteInfo = await res.json();
        setInfo(data);
        if (data.is_expired) setPageState('expired');
        else if (data.is_claimed) setPageState('claimed');
        else setPageState('ready');
      } catch {
        if (!cancelled) setPageState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-suggest slug from org name
  useEffect(() => {
    setOrgForm((prev) => ({
      ...prev,
      orgSlug: slugify(prev.orgName),
    }));
  }, [orgForm.orgName]);

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setActing(true);
    setActionError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/v1/public/people/invites/${encodeURIComponent(token)}/accept`,
        {
          method: 'POST',
          headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { detail?: string }).detail ?? 'Failed to accept the invite. Please try again.',
        );
      }

      // Phase 3: check if user has an org via forced token refresh
      const tokenResult = await auth.currentUser?.getIdTokenResult(true);
      const hasOrg = !!(tokenResult?.claims?.organisationId);

      if (hasOrg) {
        setPageState('accepted');
      } else {
        setPageState('accepted_needs_org');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setActing(false);
    }
  }, [token]);

  const handleDecline = useCallback(async () => {
    if (!token) return;
    setActing(true);
    setActionError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/public/people/invites/${encodeURIComponent(token)}/decline`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { detail?: string }).detail ?? 'Failed to decline the invite. Please try again.',
        );
      }
      setPageState('declined');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setActing(false);
    }
  }, [token]);

  const handleRegisterOrg = useCallback(async () => {
    if (!token) return;
    setOrgRegistering(true);
    setOrgError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`${API_BASE_URL}/v1/public/organisations/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({
          invite_token: token,
          turnstile_token: 'dev-bypass',
          org_name: orgForm.orgName,
          org_slug: orgForm.orgSlug,
          org_types: [orgForm.orgType],
          consent_terms: orgForm.consentTerms,
          consent_privacy: orgForm.consentPrivacy,
          consent_marketing: orgForm.consentMarketing,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { detail?: string }).detail ?? 'Failed to create organisation. Please try again.',
        );
      }
      const result = await res.json() as { org_slug: string };
      setRegisteredSlug(result.org_slug);
      // Force token refresh so the new custom claims are picked up
      await auth.currentUser?.getIdToken(true);
      setPageState('org_registered');
    } catch (err) {
      setOrgError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setOrgRegistering(false);
    }
  }, [token, orgForm]);

  if (authLoading || pageState === 'loading') {
    return (
      <div className="login-page">
        <div className="login-container">
          <p className="login-subtitle">Loading invite…</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error' || !token) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">Invalid invite link</h1>
          <p className="login-subtitle">
            This invite link is not valid. Check the link in your email or contact the sender.
          </p>
          <div className="login-toggle">
            <Link to="/" className="link-button">Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'expired') {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">Invite expired</h1>
          <p className="login-subtitle">
            This invite link has expired. Contact{' '}
            <strong>{info?.inviter_org_name ?? 'the sender'}</strong> to request a new one.
          </p>
          <div className="login-toggle">
            <Link to="/" className="link-button">Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'claimed') {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">Invite already used</h1>
          <p className="login-subtitle">This invite has already been responded to.</p>
          <div className="login-toggle">
            {isAuthenticated ? (
              <Link to="/redirect" className="link-button">Go to your dashboard</Link>
            ) : (
              <Link to="/" className="link-button">Sign in</Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'accepted') {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">Connected!</h1>
          <p className="login-subtitle">
            You are now connected with <strong>{info?.inviter_org_name}</strong> on Hire It Now.
          </p>
          <div className="login-toggle">
            <Link to="/redirect" className="link-button">Go to your dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'accepted_needs_org') {
    const canSubmit =
      orgForm.orgName.trim().length >= 2 &&
      orgForm.orgSlug.length >= 3 &&
      orgForm.consentTerms &&
      orgForm.consentPrivacy &&
      !orgRegistering;

    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1 className="login-title">Almost there!</h1>
            <p className="login-subtitle">
              Set up your organisation to complete the connection with{' '}
              <strong>{info?.inviter_org_name}</strong>.
            </p>
          </div>

          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label htmlFor="org-name" style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '14px' }}>
                Organisation name
              </label>
              <input
                id="org-name"
                type="text"
                className="form-input"
                value={orgForm.orgName}
                onChange={(e) => setOrgForm((f) => ({ ...f, orgName: e.target.value }))}
                placeholder="e.g. Acme Equipment Ltd"
                maxLength={255}
              />
            </div>

            <div>
              <label htmlFor="org-slug" style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '14px' }}>
                URL identifier
              </label>
              <input
                id="org-slug"
                type="text"
                className="form-input"
                value={orgForm.orgSlug}
                onChange={(e) => setOrgForm((f) => ({ ...f, orgSlug: e.target.value.toLowerCase() }))}
                placeholder="e.g. acme-equipment"
                maxLength={50}
              />
              <p style={{ fontSize: '12px', color: 'var(--color-muted, #6b7280)', marginTop: '4px' }}>
                Lowercase letters, numbers and hyphens only. Cannot be changed later.
              </p>
            </div>

            <div>
              <label htmlFor="org-type" style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '14px' }}>
                Organisation type
              </label>
              <select
                id="org-type"
                className="form-input"
                value={orgForm.orgType}
                onChange={(e) => setOrgForm((f) => ({ ...f, orgType: e.target.value as OrgType }))}
              >
                <option value="hirer">Hirer (equipment hire customer)</option>
                <option value="partner">Partner / Broker</option>
                <option value="supplier">Supplier</option>
              </select>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
                Before you create your account, please review:
              </p>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={orgForm.consentTerms}
                  onChange={(e) => setOrgForm((f) => ({ ...f, consentTerms: e.target.checked }))}
                  style={{ marginTop: '2px' }}
                />
                <span style={{ fontSize: '13px' }}>
                  I agree to the{' '}
                  <a href={import.meta.env.VITE_LEGAL_TERMS_URL ?? '#'} target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{' '}
                  <strong>(required)</strong>
                </span>
              </label>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={orgForm.consentPrivacy}
                  onChange={(e) => setOrgForm((f) => ({ ...f, consentPrivacy: e.target.checked }))}
                  style={{ marginTop: '2px' }}
                />
                <span style={{ fontSize: '13px' }}>
                  I have read and understood the{' '}
                  <a href={import.meta.env.VITE_LEGAL_PRIVACY_URL ?? '#'} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>{' '}
                  <strong>(required)</strong>
                </span>
              </label>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={orgForm.consentMarketing}
                  onChange={(e) => setOrgForm((f) => ({ ...f, consentMarketing: e.target.checked }))}
                  style={{ marginTop: '2px' }}
                />
                <span style={{ fontSize: '13px' }}>
                  I&apos;m happy to receive product updates and communications from Hire It Now (optional)
                </span>
              </label>
            </div>

            {orgError && (
              <div className="login-error" role="alert">
                <span className="error-icon" aria-hidden="true">!</span>
                <div className="error-content">
                  <p className="error-message">{orgError}</p>
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary btn-signin"
              onClick={handleRegisterOrg}
              disabled={!canSubmit}
              aria-busy={orgRegistering}
            >
              {orgRegistering ? 'Creating…' : 'Create organisation'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'org_registered') {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">You&apos;re all set!</h1>
          <p className="login-subtitle">
            Your organisation has been created and you are connected with{' '}
            <strong>{info?.inviter_org_name}</strong> on Hire It Now.
          </p>
          {registeredSlug && (
            <p className="login-subtitle" style={{ marginTop: '8px' }}>
              Slug: <code>{registeredSlug}</code>
            </p>
          )}
          <div className="login-toggle" style={{ marginTop: '24px' }}>
            <Link to="/redirect" className="btn btn-primary btn-signin">
              Go to your dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'declined') {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">Invite declined</h1>
          <p className="login-subtitle">
            You have declined the connection request from{' '}
            <strong>{info?.inviter_org_name}</strong>.
          </p>
        </div>
      </div>
    );
  }

  // pageState === 'ready' — show accept / decline prompt
  const returnTo = `/invite?token=${encodeURIComponent(token)}`;

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Connection request</h1>
          <p className="login-subtitle">
            <strong>{info?.inviter_org_name}</strong> has added you as a contact on Hire It Now
            and would like to connect.
          </p>
        </div>

        {isAuthenticated ? (
          <>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                className="btn btn-primary btn-signin"
                onClick={handleAccept}
                disabled={acting}
                aria-busy={acting}
              >
                {acting ? 'Processing…' : 'Accept'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDecline}
                disabled={acting}
              >
                Decline
              </button>
            </div>
            {actionError && (
              <div className="login-error" role="alert" style={{ marginTop: '16px' }}>
                <span className="error-icon" aria-hidden="true">!</span>
                <div className="error-content">
                  <p className="error-message">{actionError}</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ marginTop: '24px' }}>
            <p className="login-subtitle" style={{ marginBottom: '16px' }}>
              Sign in to accept or decline this invite.
            </p>
            <Link
              to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
              className="btn btn-primary btn-signin"
            >
              Sign in to respond
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
